import { useLayoutEffect, useRef, useState } from "react";
import { countVisibleDesktopNavItems, DESKTOP_NAV_ITEM_GAP_PX } from "./desktopNav";

type UseDesktopNavOverflowInput = {
  itemCount: number;
  fallbackVisibleCount: number;
  measureKey: string;
};

export function useDesktopNavOverflow({ itemCount, fallbackVisibleCount, measureKey }: UseDesktopNavOverflowInput) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(fallbackVisibleCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || itemCount === 0) {
      setVisibleCount(itemCount === 0 ? 0 : fallbackVisibleCount);
      return;
    }

    const update = () => {
      const available = container.clientWidth;
      const itemWidths = Array.from(
        measure?.querySelectorAll<HTMLElement>("[data-desktop-nav-measure-item]") ?? []
      ).map((element) => element.offsetWidth);
      const moreWidth = measure?.querySelector<HTMLElement>("[data-desktop-nav-measure-more]")?.offsetWidth ?? 0;

      if (available === 0 || itemWidths.length === 0 || itemWidths.every((width) => width === 0)) {
        setVisibleCount((current) => (current === fallbackVisibleCount ? current : fallbackVisibleCount));
        return;
      }

      const nextVisibleCount = countVisibleDesktopNavItems({
        itemWidths,
        moreWidth,
        gap: DESKTOP_NAV_ITEM_GAP_PX,
        available
      });
      setVisibleCount((current) => (current === nextVisibleCount ? current : nextVisibleCount));
    };

    update();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fallbackVisibleCount, itemCount, measureKey]);

  return { containerRef, measureRef, visibleCount };
}
